//? React
import { useState } from "react";

//? External
import { Button, Spinner, Form, Card } from "react-bootstrap";
import { FaListUl, FaPlus, FaTrashAlt } from "react-icons/fa";

//* Sanitization
import { parseHtmlToText } from "../../../utils/questions/questionParseText";
import DOMPurify from "dompurify";

//* Data hooks
import { useQuestionCart } from "../../../hooks/questionCart/useQuestionCart";
import { useQuestions } from "../../../hooks/questions/useQuestions";

//? Types
import { QuestionDetails } from "../../../hooks/questions/types/types";

interface ExamCartSectionProps {
  token: string;
  existingQuestionIds: string[];
  onAddQuestion: (question: QuestionDetails) => void;
  onError: (message: string) => void;
}

/**
 * Section rendered inside the exam form. Lists the questions currently saved in
 * the persistent cart and lets the user pull selected ones into the exam.
 */
export default function ExamCartSection({
  token,
  existingQuestionIds,
  onAddQuestion,
  onError,
}: ExamCartSectionProps) {
  const { items, loading, removeFromCart, removeManyFromCart } = useQuestionCart(token);
  const { getQuestionById } = useQuestions(token);

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const toggle = (questionId: string) => {
    setSelected((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const handleRemoveOne = async (itemId: string) => {
    setRemovingId(itemId);
    try {
      await removeFromCart(itemId);
    } catch (error) {
      onError("Erro ao remover questão da lista.");
    } finally {
      setRemovingId(null);
    }
  };

  const handleRemoveSelected = async () => {
    const chosen = items.filter((item) => selected[item.questionId]);
    if (chosen.length === 0) {
      onError("Selecione ao menos uma questão da lista.");
      return;
    }

    setRemoving(true);
    try {
      await removeManyFromCart(chosen.map((item) => item.id));
      setSelected({});
    } catch (error) {
      onError("Erro ao remover questões da lista.");
    } finally {
      setRemoving(false);
    }
  };

  const handleAddSelected = async () => {
    const chosen = items.filter((item) => selected[item.questionId]);
    if (chosen.length === 0) {
      onError("Selecione ao menos uma questão da lista.");
      return;
    }

    setAdding(true);
    try {
      let ignored = 0;
      for (const item of chosen) {
        if (existingQuestionIds.includes(item.questionId)) {
          ignored += 1;
          continue;
        }

        // Busca os detalhes completos (subQuestions) necessários para a prova
        const details = await getQuestionById(item.questionId);
        if (details) {
          const cleaned: QuestionDetails = {
            ...details,
            statement: parseHtmlToText(details.statement ?? "", DOMPurify.sanitize) as string,
          };
          onAddQuestion(cleaned);
          // Remove do carrinho após adicionar à prova
          await removeFromCart(item.id);
        }
      }

      if (ignored > 0) {
        onError(`${ignored} questão(ões) já estavam na prova e foram ignoradas.`);
      }

      setSelected({});
    } catch (error: any) {
      onError(error?.message || "Erro ao adicionar questões da lista.");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-3">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="my-3">
        <Card.Body className="text-center text-muted">
          <FaListUl size={24} className="mb-2" />
          <p className="mb-0">
            Sua lista está vazia. Use o botão "📋 Lista de Questões" para
            selecionar questões.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="my-3">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <strong>
          <FaListUl className="me-2" />
          Questões na lista ({items.length})
        </strong>
        <div className="d-flex gap-2">
          <Button
            id="btn-remove-cart-selected"
            type="button"
            variant="outline-danger"
            size="sm"
            disabled={removing}
            onClick={handleRemoveSelected}
          >
            <FaTrashAlt className="me-1" />
            {removing ? "Removendo..." : "Remover selecionadas"}
          </Button>
          <Button
            id="btn-add-cart-selected-to-exam"
            type="button"
            variant="success"
            size="sm"
            disabled={adding}
            onClick={handleAddSelected}
          >
            <FaPlus className="me-1" />
            {adding ? "Adicionando..." : "Adicionar selecionadas à prova"}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="d-flex flex-column gap-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="d-flex align-items-center justify-content-between"
          >
            <Form.Check
              type="checkbox"
              id={`cart-check-${item.id}`}
              checked={!!selected[item.questionId]}
              onChange={() => toggle(item.questionId)}
              label={
                <span>
                  <strong>{item.question.displayLabel ?? "Questão"}</strong>
                  <small className="text-muted ms-2">
                    {item.question.discipline?.name ?? "-"}
                    {item.question.year ? ` · ${item.question.year}` : ""}
                  </small>
                </span>
              }
            />
            <Button
              id={`btn-remove-cart-item-${item.id}`}
              type="button"
              variant="outline-danger"
              size="sm"
              aria-label="Remover questão da lista"
              disabled={removingId === item.id}
              onClick={() => handleRemoveOne(item.id)}
            >
              <FaTrashAlt />
            </Button>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}
